import React, { useEffect, useRef, useState } from "react";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let scriptPromise = null;

function loadGoogle() {
  if (window.google?.maps?.places) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places&language=fr&region=FR`;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Reusable French address field. Uses Google Places Autocomplete when a Maps API
// key is configured (REACT_APP_GOOGLE_MAPS_API_KEY), otherwise falls back to a plain
// verified-format text input so the app keeps working until the key is provided.
export default function AddressAutocomplete({ value, onChange, onResolved, placeholder = "Adresse", testid = "address-input", city, className = "" }) {
  const inputRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!KEY) return;
    let ac;
    loadGoogle().then(() => {
      if (!inputRef.current || !window.google?.maps?.places) return;
      ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "fr" },
        fields: ["formatted_address", "address_components", "geometry", "name"],
        types: ["geocode", "establishment"],
      });
      ac.addListener("place_changed", () => {
        const p = ac.getPlace();
        if (!p) return;
        const comp = {};
        (p.address_components || []).forEach((c) => { c.types.forEach((t) => { comp[t] = c.long_name; comp[`${t}_short`] = c.short_name; }); });
        const resolved = {
          formatted: p.formatted_address || inputRef.current.value,
          city: comp.locality || comp.postal_town || comp.administrative_area_level_2 || "",
          postal: comp.postal_code || "",
          lat: p.geometry?.location?.lat?.() || null,
          lng: p.geometry?.location?.lng?.() || null,
        };
        setVerified(true);
        onChange?.(resolved.formatted);
        onResolved?.(resolved);
      });
      setReady(true);
    }).catch(() => setReady(false));
    return () => { if (ac && window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(ac); };
  }, []); // eslint-disable-line

  const mapsQuery = encodeURIComponent(`${value || ""} ${city || ""}`.trim());

  return (
    <div className={className}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          ref={inputRef}
          data-testid={testid}
          className="pl-9"
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => { setVerified(false); onChange?.(e.target.value); }}
        />
        {verified && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" data-testid={`${testid}-verified`} />}
      </div>
      {KEY ? (
        verified && <p className="text-[11px] text-emerald-600 mt-1">Adresse vérifiée par Google Maps</p>
      ) : (
        <p className="text-[11px] text-muted-foreground mt-1">Vérification Google Maps inactive (clé API à configurer). Saisie manuelle acceptée.</p>
      )}
      {value && (
        <iframe title={`map-${testid}`} className="w-full h-40 rounded-lg border-0 mt-2"
          src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`} data-testid={`${testid}-map`} />
      )}
    </div>
  );
}
